
/**
 * Calculates the duration between two dates and returns a formatted string
 * 
 * @param startDate Start date in YYYY-MM format
 * @param endDate End date in YYYY-MM format or null if current
 * @param isCurrent Whether the position is current
 * @returns Formatted duration string (e.g., "2 years, 3 months")
 */
export function calculateDuration(
  startDate: string, 
  endDate?: string | null, 
  isCurrent: boolean = false
): string {
  if (!startDate) return '';
  
  try {
    // Always use the current date for current positions to ensure duration updates over time
    const start = new Date(startDate + '-01'); // Add day for proper parsing
    const end = isCurrent ? new Date() : endDate ? new Date(endDate + '-01') : new Date();
    
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    
    if (months < 0) {
      years--;
      months += 12;
    }
    
    if (years === 0) {
      return months === 1 ? '1 month' : `${months} months`;
    } else if (years === 1 && months === 0) {
      return '1 year';
    } else if (months === 0) {
      return `${years} years`;
    } else {
      return `${years} year${years > 1 ? 's' : ''}, ${months} month${months > 1 ? 's' : ''}`;
    }
  } catch {
    return '';
  }
}

/**
 * Returns a short version of the duration for display in timeline nodes
 * 
 * @param duration Full duration string
 * @returns Short duration string with full unit names (e.g., "2 years" or "11 months")
 */
export function getShortDuration(duration: string): string {
  if (duration.includes('year')) {
    // Extract the year portion for display
    const yearMatch = duration.match(/(\d+) year/);
    if (yearMatch && yearMatch[1]) {
      const years = parseInt(yearMatch[1]);
      return `${years} ${years === 1 ? 'year' : 'years'}`;
    }
    return duration.split(',')[0]; // Fallback to previous behavior
  } else if (duration.includes('month')) {
    // Extract the month portion for display
    const monthMatch = duration.match(/(\d+) month/);
    if (monthMatch && monthMatch[1]) {
      const months = parseInt(monthMatch[1]);
      return `${months} ${months === 1 ? 'month' : 'months'}`;
    }
  }
  return duration;
}
